import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemMultiUserValue } from '@/common/api/types/items';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { MultiValueFieldManager, type MultiValueFieldValue } from './MultiValueFieldManager';
import { UserInlineChip } from './UserInlineChip';
import { UserSelectionDialog } from './UserSelectionDialog';
import { MULTI_VALUE_MAX_COUNT } from './validation';

interface MultiUserFieldProps {
  name: string;
  values?: ItemMultiUserValue[];
  isEditable?: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  onChange: (userIds: string[]) => void;
  onValidationChange?: (error: string | null) => void;
}

export const MultiUserField = ({
  name,
  values = [],
  isEditable = false,
  autoFocus = false,
  isPending = false,
  isSuccess = false,
  isError,
  error,
  onChange,
  onValidationChange,
}: MultiUserFieldProps) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(autoFocus && values.length === 0);

  const handleValidationError = (err: string | null) => {
    setValidationError(err);
    onValidationChange?.(err);
  };

  const selectedUserIds = React.useMemo(() => values.map((v) => v.MULTI_USER), [values]);

  const handleOnAdd = (userId: string) => {
    if (selectedUserIds.includes(userId)) return;
    if (values.length >= MULTI_VALUE_MAX_COUNT) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiUserMaxCount, {
          maxCount: MULTI_VALUE_MAX_COUNT,
        }),
      );
      return;
    }
    handleValidationError(null);
    onChange([...selectedUserIds, userId]);
  };

  const fieldValues: MultiValueFieldValue<ItemMultiUserValue>[] = values.map((v) => ({
    id: v.id,
    value: v,
  }));

  const resolvedIsError = !!validationError || !!isError;
  const resolvedError = validationError ? new Error(validationError) : (error ?? null);

  return (
    <FieldWithStatusIndicator name={name} isPending={isPending} isSuccess={isSuccess}>
      <div className="flex flex-col gap-1">
        <MultiValueFieldManager
          values={fieldValues}
          isEditable={isEditable}
          disabled={isPending}
          onRemove={(id) => onChange(values.filter((v) => v.id !== id).map((v) => v.MULTI_USER))}
          maxCount={MULTI_VALUE_MAX_COUNT}
          onAddClick={() => setDialogOpen(true)}
        >
          {(item) => <UserInlineChip userId={item.value.MULTI_USER} />}
        </MultiValueFieldManager>
        {resolvedIsError && resolvedError && (
          <span className="text-xs text-text-error" role="alert">
            {resolvedError.message}
          </span>
        )}
      </div>

      <UserSelectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        excludedUserIds={selectedUserIds}
        onSave={(userId) => {
          handleOnAdd(userId);
          setDialogOpen(false);
        }}
        title={t(($) => $.adaptivePlanner.itemPanel.multiUserField.addUser)}
      />
    </FieldWithStatusIndicator>
  );
};
